import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Trash2,
  Pencil,
  ArrowLeft,
  Users2,
  Phone,
  X,
  Loader2 as Loader2Icon,
  CalendarDays,
  Clock3,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import PhoneInput from "@/components/PhoneInput";
import { isSubmittablePhone } from "@/lib/phone";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const EXPERTISE_LEVELS = [
  { v: "expert",   label: "Expert",   sub: "Highest",  stars: 3 },
  { v: "advanced", label: "Advanced", sub: "Mid",      stars: 2 },
  { v: "standard", label: "Standard", sub: "Baseline", stars: 1 },
];

function ExpertiseStars({ n, className = "" }) {
  const filled = "★".repeat(n);
  const empty = "★".repeat(Math.max(0, 3 - n));
  return (
    <span className={`inline-block tracking-[0.05em] ${className}`} aria-label={`${n} of 3`}>
      <span className="text-[#F3C55A]">{filled}</span>
      <span className="text-nexa-line">{empty}</span>
    </span>
  );
}

function ExpertiseBadge({ level, testId }) {
  const cfg = EXPERTISE_LEVELS.find((e) => e.v === (level || "standard").toLowerCase()) || EXPERTISE_LEVELS[2];
  return (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 h-4 px-1.5 rounded-full bg-nexa-canvas border border-nexa-line text-[8.5px] font-medium text-nexa-mute"
      title={`${cfg.label} expertise`}
    >
      <ExpertiseStars n={cfg.stars} className="text-[8px]" />
      <span>{cfg.label}</span>
    </span>
  );
}

export default function TeamMembers() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null); // null = loading
  const [editing, setEditing] = useState(null); // member object or "new" | null

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/team");
      setRows(data.members || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load team");
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (m) => {
    if (!confirm(`Remove ${m.name} from your team?`)) return;
    try {
      await api.delete(`/team/${m.member_id}`);
      toast.success("Team member removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const activeCount = useMemo(() => (rows || []).filter((r) => r.active).length, [rows]);

  return (
    <div className="min-h-screen bg-[#F7F6F0]">
      <Sidebar />
      <main className="nexa-compact lg:ml-[260px] min-h-screen">
        <div className="w-full max-w-[1080px] mx-auto px-4 md:px-8 py-6 md:py-10">
          {/* Breadcrumb */}
          <button
            onClick={() => nav("/business-settings")}
            className="flex items-center gap-1.5 text-[10.5px] text-[#102C2D] hover:text-[#075B59] mb-4 transition-colors"
            data-testid="tm-back-btn"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.6} /> Business Settings
          </button>

          {/* Header */}
          <motion.header
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
            className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6"
          >
            <div>
              <h1 className="mt-1 text-[18px] md:text-[19px] font-semibold tracking-[-0.02em] text-nexa-ink">
                Your <span className="italic">roster</span>.
              </h1>
              <p className="mt-1 text-[11px] text-nexa-mute max-w-[54ch]">
                The people Nexa can assign appointments to. Manage availability and services here.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="lux-card px-3 py-2 text-[10.5px] text-nexa-mute">
                <span className="text-nexa-ink font-semibold">{activeCount}</span> active
                {rows && (
                  <span className="text-nexa-mute"> · {rows.length} total</span>
                )}
              </div>
              <button
                onClick={() => setEditing("new")}
                className="lux-btn-accent"
                data-testid="tm-add-btn"
              >
                <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> Add team member
              </button>
            </div>
          </motion.header>

          {/* List */}
          <section className="space-y-2.5">
            {rows === null && [1, 2, 3].map((i) => (
              <div key={i} className="lux-card p-3.5">
                <div className="lux-skel h-3 w-1/3 mb-2" />
                <div className="lux-skel h-2.5 w-2/3" />
              </div>
            ))}

            {rows && rows.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="lux-card p-10 text-center"
                data-testid="tm-empty"
              >
                <div className="mx-auto mb-4 w-9 h-9 rounded-full bg-nexa-canvas flex items-center justify-center">
                  <Users2 className="w-4 h-4 text-nexa-mute" strokeWidth={1.5} />
                </div>
                <h3 className="text-[13px] font-semibold text-nexa-ink">No team members yet.</h3>
                <p className="mt-1.5 text-[11px] text-nexa-mute max-w-[40ch] mx-auto">
                  Add the first person Nexa can assign appointments to.
                </p>
                <button onClick={() => setEditing("new")} className="lux-btn-accent mt-5" data-testid="tm-empty-cta">
                  <Plus className="w-3 h-3 mr-1" strokeWidth={2} /> Add team member
                </button>
              </motion.div>
            )}

            {rows && rows.map((m) => (
              <TeamRow key={m.member_id} m={m} onEdit={() => setEditing(m)} onDelete={() => remove(m)} />
            ))}
          </section>
        </div>
      </main>

      <AnimatePresence>
        {editing && (
          <MemberModal
            member={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function TeamRow({ m, onEdit, onDelete }) {
  const hrs = m.working_hours || {};
  const daysStr = (m.working_days || []).join(" · ") || "—";
  const svcs = (m.services || []).slice(0, 3);
  const svcMore = (m.services || []).length - svcs.length;

  const initials =
    (m.name || "•")
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join("")
      .toUpperCase() || "•";

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="group relative overflow-hidden rounded-2xl border border-[#E4E8E0] border-l-[3px] border-l-[#D8B45A] bg-white shadow-[0_4px_16px_rgba(16,44,45,0.035)] transition-all duration-300 hover:border-[#B8DCD0] hover:border-l-[#D8B45A] hover:shadow-[0_10px_28px_rgba(7,91,89,0.075)]"
      data-testid={`tm-row-${m.member_id}`}
    >
      <div className="flex items-center gap-3 px-4 py-2 md:gap-3 md:px-4 md:py-1">
        {/* Profile photo */}
        <div className="relative shrink-0">
          {m.profile_image ? (
            <img
              src={m.profile_image}
              alt={m.name}
              className="h-10 w-10 rounded-full border border-[#E0E8E3] object-cover ring-2 ring-[#F5F7F2] md:h-10 md:w-10"  
              data-testid={`tm-avatar-${m.member_id}`}
            />
          ) : (
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D9EDE5] bg-[#E8F4EF] text-sm font-semibold tracking-wide text-[#075B59] ring-4 ring-[#F5F7F2] md:h-[68px] md:w-[68px]"
              data-testid={`tm-avatar-${m.member_id}`}
            >
              {initials}
            </div>
          )}

          <span
            className={`absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-[2.5px] border-white ${
              m.active ? "bg-[#10B981]" : "bg-[#A6B0AC]"
            }`}
            title={m.active ? "Active" : "Inactive"}
          />
        </div>

        {/* Member details */}
        <div className="min-w-0 flex-1">
          {/* Name, role, expertise and status */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <h3 className="truncate text-[12px] md:text-[13px] font-semibold tracking-[-0.02em] text-[#102C2D]">
              {m.name}
            </h3>

            {m.role && (
              <span className="text-[11px] font-medium text-[#71817C]">
                {m.role}
              </span>
            )}

            <ExpertiseBadge
              level={m.expertise_level}
              testId={`tm-expertise-${m.member_id}`}
            />

            {m.active ? (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border border-[#C7E7D7] bg-[#E8F4EF] px-2.5 py-1 text-[10px] font-medium text-[#087F63]"
                data-testid={`tm-status-${m.member_id}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                Active
              </span>
            ) : (
              <span
                className="inline-flex items-center rounded-full border border-[#E0E8E3] bg-[#F3F5F2] px-2.5 py-1 text-[10px] font-medium text-[#71817C]"
                data-testid={`tm-status-${m.member_id}`}
              >
                Inactive
              </span>
            )}
          </div>

          {/* Contact and availability */}
          
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-[#637771] md:gap-x-4 md:text-[11px]">
            {m.phone && (
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3 w-3 shrink-0 text-[#718E83]" strokeWidth={1.7} />
                {m.phone}
              </span>
            )}

            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3 w-3 shrink-0 text-[#718E83]" strokeWidth={1.7} />
              {daysStr}
            </span>

            <span className="inline-flex items-center gap-1.5">
              <Clock3 className="h-3 w-3 shrink-0 text-[#718E83]" strokeWidth={1.7} />
              {hrs.start || "—"} – {hrs.end || "—"}
            </span>
          </div>


          {/* Assigned services */}
          {svcs.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {svcs.map((s) => (
                <span
                  key={s}
                  className="inline-flex min-h-[19px] items-center rounded-full border border-[#DFEBE5] bg-[#EFF5F0] px-2 py-0.5 text-[9px] font-medium text-[#315B55]"
                >
                  {s}
                </span>
              ))}

              {svcMore > 0 && (
                <span className="inline-flex min-h-[19px] items-center rounded-full border border-[#DFEBE5] bg-[#EFF5F0] px-2 py-0.5 text-[9px] font-medium text-[#315B55]">
                  +{svcMore} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="ml-1 flex shrink-0 items-center gap-2 border-l border-[#E8EDE7] pl-3 md:gap-2.5 md:pl-5">
          <button
            onClick={onEdit}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#DCE7E0] bg-white text-[#607A73] transition-all duration-200 hover:border-[#A8D4C5] hover:bg-[#E8F4EF] hover:text-[#075B59] md:h-11 md:w-11"
            aria-label="Edit"
            title="Edit member"
            data-testid={`tm-edit-${m.member_id}`}
          >
            <Pencil className="h-4 w-4" strokeWidth={1.7} />
          </button>

          <button
            onClick={onDelete}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#DCE7E0] bg-white text-[#71817C] transition-all duration-200 hover:border-[#F0CACA] hover:bg-[#FFF3F2] hover:text-[#C44747] md:h-11 md:w-11"
            aria-label="Delete"
            title="Remove member"
            data-testid={`tm-delete-${m.member_id}`}
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.7} />
          </button>
        </div>
      </div>
    </motion.article>
  );
}


// -------------- Photo uploader --------------
const PHOTO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB source cap
const PHOTO_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";

function PhotoUploader({ value, onChange, fallbackName }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const initials = (fallbackName || "•").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "•";

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      toast.error("Only JPG, PNG or WebP images are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > PHOTO_MAX_BYTES) {
      toast.error("Image is too large — max 2 MB.");
      e.target.value = "";
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      onChange(String(reader.result || ""));
      setUploading(false);
    };
    reader.onerror = () => {
      toast.error("Couldn't read the image.");
      setUploading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div className="mb-5 flex items-center gap-4" data-testid="tm-form-photo">
      <div className="relative">
        {value ? (
          <img
            src={value}
            alt="Preview"
            className="w-14 h-14 rounded-full object-cover border border-nexa-line"
            data-testid="tm-form-photo-preview"
          />
        ) : (
          <div
            className="w-14 h-14 rounded-full bg-nexa-canvas border border-nexa-line flex items-center justify-center text-[15px] font-medium text-nexa-ink"
            data-testid="tm-form-photo-preview"
          >
            {initials}
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
            <Loader2Icon className="w-4 h-4 animate-spin text-nexa-accent" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium text-nexa-ink">Profile photo</div>
        <div className="text-[9.5px] text-nexa-mute mt-0.5">
          JPG, PNG or WebP · optional · max 2 MB.
        </div>
        <div className="mt-1.5 flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="h-6 px-2.5 rounded-full bg-nexa-canvas border border-nexa-line text-[10px] font-medium text-nexa-ink hover:border-nexa-ink/40 transition-colors"
            data-testid="tm-form-photo-upload"
          >
            {value ? "Change photo" : "Upload photo"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="h-6 px-2.5 rounded-full bg-white border border-nexa-line text-[10px] font-medium text-nexa-mute hover:text-red-500 hover:border-red-200 transition-colors"
              data-testid="tm-form-photo-remove"
            >
              Remove
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          onChange={onPick}
          className="hidden"
          data-testid="tm-form-photo-input"
        />
      </div>
    </div>
  );
}

// -------------- Add / Edit modal --------------
function MemberModal({ member, onClose, onSaved }) {
  const isEdit = !!member;
  const [form, setForm] = useState(() => ({
    name: member?.name || "",
    role: member?.role || "",
    phone: member?.phone || "",
    service_ids: new Set(member?.service_ids || []),
    working_days: new Set(member?.working_days || ["Mon", "Tue", "Wed", "Thu", "Fri"]),
    start: member?.working_hours?.start || "09:00",
    end: member?.working_hours?.end || "18:00",
    expertise_level: (member?.expertise_level || "standard").toLowerCase(),
    profile_image: member?.profile_image || "",
    active: member?.active !== false,
  }));
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState(null);          // list of active services
  const [svcQuery, setSvcQuery] = useState("");
  const [svcOpen, setSvcOpen] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/services", { params: { active_only: true } });
        setServices(data.services || []);
      } catch { setServices([]); }
    })();
  }, []);

  const toggleDay = (d) => {
    const next = new Set(form.working_days);
    if (next.has(d)) next.delete(d); else next.add(d);
    update("working_days", next);
  };

  const toggleService = (id) => {
    const next = new Set(form.service_ids);
    if (next.has(id)) next.delete(id); else next.add(id);
    update("service_ids", next);
  };

  const filteredServices = (services || []).filter((s) =>
    !svcQuery.trim() || s.name.toLowerCase().includes(svcQuery.toLowerCase()),
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.phone && !isSubmittablePhone(form.phone, { required: false })) {
      toast.error("Invalid phone");
      return;
    }
    if (form.service_ids.size === 0) {
      toast.error("Assign at least one service");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        role: form.role.trim() || null,
        phone: form.phone,
        service_ids: Array.from(form.service_ids),
        working_days: DAYS.filter((d) => form.working_days.has(d)),
        working_hours: { start: form.start, end: form.end },
        expertise_level: form.expertise_level,
        profile_image: form.profile_image || "",
        active: form.active,
      };
      if (isEdit) {
        await api.patch(`/team/${member.member_id}`, payload);
        toast.success("Team member updated");
      } else {
        await api.post("/team", payload);
        toast.success("Team member added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-nexa-accent/40 backdrop-blur-[2px] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="tm-modal"
    >
      <motion.form
        initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3, ease: [0.2, 0.7, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="lux-card w-full max-w-xl p-6 relative max-h-[85vh] overflow-y-auto"
      >
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-nexa-mute hover:text-nexa-ink" aria-label="Close">
          <X className="w-4 h-4" strokeWidth={1.7} />
        </button>

        <div className="lux-eyebrow">{isEdit ? "Edit" : "Add"}</div>
        <h2 className="text-[16px] font-semibold tracking-tight text-nexa-ink mt-1 mb-5">
          {isEdit ? member.name : "Add team member"}
        </h2>

        {/* Photo uploader — always at the top of the form */}
        <PhotoUploader
          value={form.profile_image}
          onChange={(dataUri) => update("profile_image", dataUri || "")}
          fallbackName={form.name || member?.name}
        />

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label">Name</label>
              <input data-testid="tm-form-name" className="lux-input" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <label className="lux-label">Role</label>
              <input data-testid="tm-form-role" className="lux-input" value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="Stylist, Therapist…" />
            </div>
          </div>

          <div>
            <label className="lux-label">Expertise level <span className="text-red-500">*</span></label>
            <div className="mt-1.5 grid grid-cols-3 gap-2" data-testid="tm-form-expertise">
              {EXPERTISE_LEVELS.map((e) => {
                const on = form.expertise_level === e.v;
                return (
                  <button
                    type="button"
                    key={e.v}
                    onClick={() => update("expertise_level", e.v)}
                    data-testid={`tm-expertise-${e.v}`}
                    className={`rounded-xl border px-3 py-2 text-left transition-all ${
                      on
                        ? "border-nexa-ink bg-nexa-canvas"
                        : "border-nexa-line bg-white hover:border-nexa-ink/40"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <ExpertiseStars n={e.stars} className="text-[10px]" />
                      <span className="text-[11px] font-medium text-nexa-ink">{e.label}</span>
                    </div>
                    <div className="mt-0.5 text-[9.5px] text-nexa-mute">{e.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="lux-label">Phone</label>
            <PhoneInput value={form.phone} onChange={(v) => update("phone", v)} testId="tm-form-phone" required={false} />
          </div>

          <div>
            <label className="lux-label">Services offered <span className="text-red-500">*</span></label>
            {services === null ? (
              <div className="lux-input flex items-center text-[11px] text-nexa-mute">Loading services…</div>
            ) : services.length === 0 ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3.5 py-2.5 text-[10.5px] text-amber-800">
                No active services yet. <a href="/business-settings/services" className="underline font-medium">Add a service</a> first.
              </div>
            ) : (
              <div data-testid="tm-form-services">
                {/* Selected chips */}
                {form.service_ids.size > 0 && (
                  <div className="flex items-center flex-wrap gap-1.5 mb-2">
                    {Array.from(form.service_ids).map((id) => {
                      const s = services.find((x) => x.service_id === id);
                      if (!s) return null;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => toggleService(id)}
                          data-testid={`tm-svc-chip-${id}`}
                          className="inline-flex items-center gap-1.5 h-6 pl-2.5 pr-1.5 rounded-full bg-nexa-accent text-white text-[10px] font-medium hover:bg-nexa-ink transition-colors"
                        >
                          {s.name}
                          <X className="w-2.5 h-2.5" strokeWidth={2.2} />
                        </button>
                      );
                    })}
                  </div>
                )}
                {/* Search + toggle picker */}
                <div className="relative">
                  <input
                    type="text"
                    value={svcQuery}
                    onChange={(e) => { setSvcQuery(e.target.value); setSvcOpen(true); }}
                    onFocus={() => setSvcOpen(true)}
                    onBlur={() => setTimeout(() => setSvcOpen(false), 150)}
                    placeholder="Search services to add…"
                    className="lux-input"
                    data-testid="tm-form-services-search"
                  />
                  {svcOpen && (
                    <div className="absolute z-30 mt-1 w-full rounded-xl border border-nexa-line bg-white shadow-lg max-h-56 overflow-y-auto py-1" data-testid="tm-form-services-list">
                      {filteredServices.length === 0 ? (
                        <div className="px-3 py-2 text-[10.5px] text-nexa-mute">No matching service.</div>
                      ) : (
                        filteredServices.map((s) => {
                          const on = form.service_ids.has(s.service_id);
                          return (
                            <button
                              key={s.service_id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => toggleService(s.service_id)}
                              data-testid={`tm-svc-opt-${s.service_id}`}
                              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[11px] transition-colors ${on ? "bg-nexa-canvas text-nexa-ink" : "hover:bg-nexa-canvas text-nexa-ink"}`}
                            >
                              <span className="truncate">{s.name}</span>
                              <span className="flex items-center gap-2 text-[9.5px] text-nexa-mute shrink-0">
                                <span>{s.duration_min} min · ₹{Number(s.amount || 0).toLocaleString()}</span>
                                {on && <span className="text-emerald-600">✓</span>}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                <p className="mt-1 text-[9.5px] text-nexa-mute">Tap a service to add or remove.</p>
              </div>
            )}
          </div>

          <div>
            <label className="lux-label">Working days</label>
            <div className="mt-1.5 flex items-center flex-wrap gap-1.5" data-testid="tm-form-days">
              {DAYS.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(d)}
                  data-testid={`tm-day-${d}`}
                  className={`h-6 px-2.5 rounded-full text-[10px] border transition-colors ${
                    form.working_days.has(d)
                      ? "bg-nexa-accent text-white border-nexa-ink"
                      : "bg-nexa-card text-nexa-mute border-nexa-line hover:text-nexa-ink"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="lux-label">Starts at</label>
              <input required type="time" data-testid="tm-form-start" className="lux-input" value={form.start} onChange={(e) => update("start", e.target.value)} />
            </div>
            <div>
              <label className="lux-label">Ends at</label>
              <input required type="time" data-testid="tm-form-end" className="lux-input" value={form.end} onChange={(e) => update("end", e.target.value)} />
            </div>
          </div>

          {/* Active toggle */}
          <label className="flex items-center justify-between gap-3 rounded-xl border border-nexa-line px-3.5 py-2.5 cursor-pointer hover:bg-nexa-canvas/60 transition-colors">
            <div>
              <div className="text-[11.5px] text-nexa-ink font-medium">Active</div>
              <div className="text-[9.5px] text-nexa-mute mt-0.5">Inactive members are hidden from appointment assignment.</div>
            </div>
            <button
              type="button" role="switch" aria-checked={form.active}
              onClick={() => update("active", !form.active)}
              data-testid="tm-form-active"
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${form.active ? "bg-nexa-accent" : "bg-nexa-line"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form.active ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="lux-btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="lux-btn-accent" data-testid="tm-form-submit">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add member"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
